(function () {
  'use strict';

  /*****************************************
   * CONSTANTS & CONFIGURATION
   *****************************************/
  const SELECTORS = {
    dataNode: 'freebie-offers-data',
    cartRow: '.cart-item',
    cartBubble: 'cart-icon-bubble',
    cartBubbleSection: 'shopify-section-cart-icon-bubble',
    cartErrors: 'cart-errors',
    drawerErrors: 'CartDrawer-CartErrors'
  };

  const API_ENDPOINTS = {
    cart: '/cart.js',
    add: '/cart/add.js',
    update: '/cart/update.js',
    change: '/cart/change.js'
  };

  const TIMING = {
    debounce: 40,
    initialLoad: 300,
    batchDelay: 50,
    addDelay: 20
  };

  const FREEBIE_PROPERTY_TAG = '_freebie-item';
  const FREEBIE_OFFER_TAG = '_freebie-offer';

  // Shopify error descriptions that indicate an item is out of stock / unavailable
  const OUT_OF_STOCK_PHRASES = [
    'all units have been sold',
    'not available',
    'unavailable',
    'out of stock',
    'sold out',
    'cannot be purchased',
    'is not available in this quantity',
    'not enough in stock'
  ];

  /*****************************************
   * INITIALIZATION & VALIDATION
   *****************************************/
  const isOfferValid = (offer, index) => {
    if (!offer.enabled) {
      console.info(`[Freebie System] Offer #${index + 1} (${offer.title || 'Untitled'}) skipped: not enabled`);
      return false;
    }

    const rawTriggers = offer.trigger_products || [];
    offer.trigger_products = rawTriggers.filter(
      id => id !== null && id !== undefined && id !== '' && (typeof id === 'number' || !isNaN(Number(id)))
    );
    if (rawTriggers.length > 0 && offer.trigger_products.length === 0) {
      console.warn(`[Freebie System] Offer #${index + 1} (${offer.title || 'Untitled'}) skipped: all trigger products are invalid`);
      return false;
    }

    const rawFreebies = offer.freebies || [];
    offer.freebies = rawFreebies.filter(
      id => id !== null && id !== undefined && id !== '' && (typeof id === 'number' || !isNaN(Number(id)))
    );
    if (offer.freebies.length === 0) {
      console.warn(`[Freebie System] Offer #${index + 1} (${offer.title || 'Untitled'}) skipped: no valid freebie products`);
      return false;
    }

    return true;
  };

  const initializeFreebieSystem = () => {
    const dataNode = document.getElementById(SELECTORS.dataNode);

    if (!dataNode) {
      return null;
    }

    try {
      const config = JSON.parse(dataNode.textContent || '{}');
      const offers = config.offers || [];

      if (!offers.length) {
        return null;
      }

      const validOffers = offers.filter((offer, index) => isOfferValid(offer, index));

      if (!validOffers.length) {
        console.info('[Freebie System] No valid/enabled offers found');
        return null;
      }

      return validOffers;
    } catch (error) {
      console.error('[Freebie System] Failed to parse configuration:', error);
      return null;
    }
  };

  /*****************************************
   * OFFER CONFLICT RESOLUTION
   *****************************************/
  const resolveOfferConflicts = (offers) => {
    const usedTriggerProducts = new Set();
    const validOffers = [];
    const conflictedOffers = [];

    offers.forEach((offer, index) => {
      if (!offer.enabled) {
        return;
      }

      const triggerProducts = offer.trigger_products || [];

      const hasTriggerConflict = triggerProducts.some(productId =>
        usedTriggerProducts.has(productId)
      );

      if (hasTriggerConflict) {
        offer.__disabledByConflict = true;
        conflictedOffers.push({
          index,
          offer,
          conflicts: {
            triggers: triggerProducts.filter(id => usedTriggerProducts.has(id))
          }
        });
        return;
      }

      triggerProducts.forEach(productId => usedTriggerProducts.add(productId));

      offer.__disabledByConflict = false;
      validOffers.push(offer);
    });

    logOfferConflicts(conflictedOffers);

    return validOffers;
  };

  const logOfferConflicts = (conflicts) => {
    if (!conflicts.length) {
      return;
    }

    console.groupCollapsed(
      `%c[Freebie System] ${conflicts.length} offer(s) disabled due to conflicts`,
      'color:#ff9800;font-weight:bold;'
    );

    conflicts.forEach(({ index, offer, conflicts: reasons }) => {
      const offerTitle = offer.title || offer.name || `Offer #${index + 1}`;

      console.warn(`❌ ${offerTitle} disabled`, {
        triggerConflicts: reasons.triggers,
        offer
      });
    });

    console.groupEnd();
  };

  /*****************************************
   * UTILITY FUNCTIONS
   *****************************************/
  const delay = (milliseconds) => {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
  };

  const isFreebieItem = (item) => {
    return item?.properties?.[FREEBIE_PROPERTY_TAG] === true;
  };

  const findCartRowByKey = (itemKey) => {
    return document.querySelector(`${SELECTORS.cartRow}[data-item-key="${itemKey}"]`);
  };

  const setCartRowLoadingState = (itemKey, isLoading) => {
    const cartRow = findCartRowByKey(itemKey);

    if (cartRow) {
      cartRow.dataset.loading = isLoading ? 'true' : 'false';
    }
  };

  const toggleCartDummy = (show) => {
    const c = document.querySelector(".cart-items");
    if (!c) return;

    const id = "CartDrawer-Item-dummy";
    let d = document.getElementById(id);
    const cartDrawer = document.getElementById("CartDrawer");
    const freebieText = cartDrawer.dataset.freebieText?.trim()
      ? cartDrawer.dataset.freebieText
      : 'Free item loading...';

    if (show) {
      if (!d) {
        d = document.createElement("tr");
        d.id = id;
        d.className = "cart-item blg:tw-px-[1rem] tw-py-[1.2rem] lg:tw-pl-[1.6rem] lg:tw-pr-[2.4rem]";
        d.setAttribute("role", "row");
        d.innerHTML = `<td style="padding:1rem;color:#212121">${freebieText}</td>`;
        d.style.cssText = 'background:linear-gradient(90deg,#F4F4F4 25%,#FAFAFA 50%,#F4F4F4 75%);background-size:200% 100%;animation:skeleton 1.5s ease-in-out infinite;display:flex';

        if (!document.getElementById('skeleton-style')) {
          const s = document.createElement('style');
          s.id = 'skeleton-style';
          s.textContent = '@keyframes skeleton{0%{background-position:200% 0}100%{background-position:-200% 0}}';
          document.head.appendChild(s);
        }
      }
      c.prepend(d);
      setTimeout(() => document.getElementById(id)?.remove(), 5000);
    } else if (d) {
      d.remove();
    }
  };

  /*****************************************
   * OUT OF STOCK DETECTION
   *****************************************/

  /**
   * Checks whether a Shopify API error response indicates
   * the item is out of stock / unavailable.
   * Returns true if the error should be silently skipped.
   */
  const isOutOfStockError = (status, errorBody) => {
    // Shopify uses 422 (Unprocessable Entity) for inventory / availability errors
    if (status !== 422) {
      return false;
    }

    // Try to match against known out-of-stock phrases in the error description
    const description = (
      errorBody?.description ||
      errorBody?.message ||
      errorBody?.error ||
      ''
    ).toLowerCase();

    const items = errorBody?.items || [];
    const itemErrors = items.flatMap(item =>
      Object.values(item?.errors || {}).flat()
    ).join(' ').toLowerCase();

    const fullErrorText = `${description} ${itemErrors}`.trim();

    if (!fullErrorText) {
      // 422 with no readable body — treat as out of stock to avoid false errors
      return true;
    }

    return OUT_OF_STOCK_PHRASES.some(phrase => fullErrorText.includes(phrase));
  };

  /*****************************************
   * CART API INTERACTIONS
   *****************************************/
  const fetchCurrentCart = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.cart);

      if (!response.ok) {
        throw new Error(`Cart fetch failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[Freebie System] Cart fetch error:', error);
      throw error;
    }
  };

  const updateCartBubbleUI = (apiResponse) => {
    const bubbleHtml = apiResponse?.sections?.[SELECTORS.cartBubble];

    if (!bubbleHtml) {
      return;
    }

    const bubbleElement = document.getElementById(SELECTORS.cartBubble);

    if (!bubbleElement) {
      return;
    }

    try {
      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(bubbleHtml, 'text/html');
      const newBubbleContent = parsedDocument.getElementById(SELECTORS.cartBubbleSection);

      if (newBubbleContent) {
        bubbleElement.innerHTML = newBubbleContent.innerHTML;
      }
    } catch (error) {
      console.error('[Freebie System] Bubble update error:', error);
    }
  };

  /**
   * Attempts to add a freebie to the cart.
   * Returns { success: true, data } on success.
   * Returns { success: false, outOfStock: true } if the item is unavailable — caller should skip silently.
   * Throws for all other unexpected errors.
   */
  const addFreebieToCart = async (variantId, offerId) => {
    toggleCartDummy(true);

    const properties = { [FREEBIE_PROPERTY_TAG]: true };
    if (offerId) {
      properties[FREEBIE_OFFER_TAG] = offerId;
    }

    let response;
    try {
      response = await fetch(API_ENDPOINTS.add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties
            }
          ],
          sections: [SELECTORS.cartBubble],
          sections_url: window.location.pathname
        })
      });
    } catch (networkError) {
      toggleCartDummy(false);
      throw networkError;
    }

    // Parse the body regardless of status so we can inspect error details
    let responseBody;
    try {
      responseBody = await response.json();
    } catch {
      responseBody = {};
    }

    if (!response.ok) {
      toggleCartDummy(false);

      if (isOutOfStockError(response.status, responseBody)) {
        console.info(
          `[Freebie System] Freebie variant ${variantId} is out of stock or unavailable — skipping silently.`,
          responseBody
        );
        return { success: false, outOfStock: true };
      }

      // Unexpected error — rethrow so the caller can handle it
      throw new Error(`Add to cart failed: ${response.status} — ${JSON.stringify(responseBody)}`);
    }

    updateCartBubbleUI(responseBody);
    return { success: true, data: responseBody };
  };

  const batchRemoveCartItems = async (itemKeys) => {
    if (!itemKeys?.length) {
      return await fetchCurrentCart();
    }

    itemKeys.forEach(key => setCartRowLoadingState(key, true));
    const lastItemKey = itemKeys[itemKeys.length - 1];
    const batchItemKeys = itemKeys.slice(0, -1);

    if (batchItemKeys.length > 0) {
      try {
        const updates = batchItemKeys.reduce((acc, key) => {
          acc[key] = 0;
          return acc;
        }, {});

        const response = await fetch(API_ENDPOINTS.update, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates,
            sections: [SELECTORS.cartBubble],
            sections_url: window.location.pathname
          })
        });

        if (!response.ok) {
          throw new Error(`Cart update failed: ${response.status}`);
        }

      } catch (error) {
        console.error('[Freebie System] Batch remove error:', error);
        displayCartError();
        setTimeout(() => {
          [...batchItemKeys, lastItemKey].forEach(key => setCartRowLoadingState(key, false));
        }, 5000);
        throw error;
      }
    }

    const cartHTML = await fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        return html;
      });
    removeThemeItem(lastItemKey, cartHTML);
    return await fetchCurrentCart();
  };

  const removeThemeItem = (key, cartHTML) => {
    const row = cartHTML.querySelector(`${SELECTORS.cartRow}[data-item-key="${key}"]`);
    if (!row) return;

    const container =
      document.querySelector("cart-items") ||
      document.querySelector("cart-drawer-items");

    if (!container) {
      console.warn('[Freebie System] Cart container not found');
      return;
    }

    container.updateQuantity(row.dataset.index, 0);
  };

  const displayCartError = () => {
    const errorElement =
      document.getElementById(SELECTORS.cartErrors) ||
      document.getElementById(SELECTORS.drawerErrors);

    if (errorElement && typeof window.cartStrings !== 'undefined') {
      errorElement.textContent = window.cartStrings.error || 'An error occurred';
    }
  };

  /*****************************************
   * UPDATE FREEBIE OFFER TAG
   * Re-tags a freebie item with the correct offer ID
   *****************************************/
  const updateFreebieOfferTag = async (itemKey, newOfferId) => {
    try {
      const response = await fetch(API_ENDPOINTS.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: itemKey,
          properties: {
            [FREEBIE_PROPERTY_TAG]: true,
            [FREEBIE_OFFER_TAG]: newOfferId
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Cart change failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[Freebie System] Failed to update freebie offer tag:', error);
      throw error;
    }
  };

  /*****************************************
   * OFFER VALIDATION LOGIC
   *****************************************/
  const isOfferUnlocked = (offer, cart) => {
    const cartItems = cart.items || [];
    const triggerProducts = offer.trigger_products || [];
    const cartSubtotal = (cart.items_subtotal_price || 0) / 100;
    const minimumQuantity = Number(offer.min_quantity || 1);
    const minimumCartValue = Number(offer.min_cart_value || 0);

    switch (offer.rule_type) {
      case 'cart_value':
        return cartSubtotal >= minimumCartValue;

      case 'quantity':
        return cartItems.some(item =>
          triggerProducts.includes(item.product_id) &&
          item.quantity >= minimumQuantity
        );

      case 'product':
      default:
        return triggerProducts.some(productId =>
          cartItems.some(item => item.product_id === productId)
        );
    }
  };

  const getOfferFreebiesInCart = (offer, cart) => {
    const offerFreebieIds = offer.freebies || [];

    return cart.items.filter(item =>
      offerFreebieIds.includes(item.variant_id) && isFreebieItem(item)
    );
  };

  const validateSingleOffer = (offer, cart) => {
    const result = {
      modified: false,
      itemKeysToRemove: [],
      freebieItemsToAdd: []
    };

    if (!offer.enabled) {
      return result;
    }

    const offerFreebieIds = offer.freebies || [];
    const isUnlocked = isOfferUnlocked(offer, cart);
    const currentFreebies = getOfferFreebiesInCart(offer, cart);

    // CASE 1: Offer is locked - remove all associated freebies
    if (!isUnlocked && offer.auto_remove && currentFreebies.length > 0) {
      result.itemKeysToRemove.push(...currentFreebies.map(item => item.key));
      result.modified = true;
    }

    // CASE 2: Offer is unlocked - clean up invalid freebies
    if (isUnlocked) {
      const validFreebieIds = new Set(offerFreebieIds);

      currentFreebies.forEach(item => {
        if (!validFreebieIds.has(item.variant_id)) {
          result.itemKeysToRemove.push(item.key);
          result.modified = true;
        }
      });

      // Remove duplicate freebies (keep only first occurrence)
      const seenVariantIds = new Set();
      currentFreebies.forEach(item => {
        if (seenVariantIds.has(item.variant_id)) {
          result.itemKeysToRemove.push(item.key);
          result.modified = true;
        }
        seenVariantIds.add(item.variant_id);
      });
    }

    // CASE 3: Offer is unlocked - add missing freebies
    if (isUnlocked && offer.auto_add) {
      const missingFreebieIds = offerFreebieIds.filter(variantId =>
        !currentFreebies.some(item => item.variant_id === variantId)
      );

      if (missingFreebieIds.length > 0) {
        result.freebieItemsToAdd.push(...missingFreebieIds.map(vid => ({ variantId: vid, offerId: offer.id })));
        result.modified = true;
      }
    }

    return result;
  };

  const validateSharedFreebies = (activeOffers, cart) => {
    const result = {
      modified: false,
      itemKeysToRemove: []
    };

    const freebieOfferCountMap = {};
    activeOffers.forEach(offer => {
      (offer.freebies || []).forEach(freebieId => {
        freebieOfferCountMap[freebieId] = (freebieOfferCountMap[freebieId] || 0) + 1;
      });
    });

    const sharedFreebieIds = Object.keys(freebieOfferCountMap)
      .filter(id => freebieOfferCountMap[id] > 1)
      .map(id => Number(id));

    if (sharedFreebieIds.length === 0) {
      return result;
    }

    sharedFreebieIds.forEach(freebieId => {
      const freebieItems = cart.items.filter(item =>
        item.variant_id === freebieId && isFreebieItem(item)
      );

      if (freebieItems.length === 0) {
        return;
      }

      const validOfferCount = activeOffers.filter(offer =>
        offer.enabled &&
        (offer.freebies || []).includes(freebieId) &&
        isOfferUnlocked(offer, cart)
      ).length;

      if (validOfferCount === 0) {
        result.itemKeysToRemove.push(...freebieItems.map(item => item.key));
        result.modified = true;
      } else if (freebieItems.length > 1) {
        result.itemKeysToRemove.push(
          ...freebieItems.slice(1).map(item => item.key)
        );
        result.modified = true;
      }
    });

    return result;
  };

  /*****************************************
   * MAIN EXECUTION
   *****************************************/
  const applyFreebieLogic = async (activeOffers, isProcessingRef) => {
    if (isProcessingRef.value) {
      return;
    }

    isProcessingRef.value = true;

    try {
      let currentCart = await fetchCurrentCart();
      const allItemKeysToRemove = [];
      const allFreebieItemsToAdd = [];
      let requiresModification = false;

      console.info(`[Freebie System] Processing ${activeOffers.length} active offer(s):`,
        activeOffers.map(o => ({ id: o.id, title: o.title, enabled: o.enabled, freebies: o.freebies, rule_type: o.rule_type }))
      );

      // VALIDATION RULE 1: Prevent freebies-only cart
      const hasRegularItems = currentCart.items.some(item => !isFreebieItem(item));

      if (currentCart.items.length > 0 && !hasRegularItems) {
        currentCart.items.forEach(item => allItemKeysToRemove.push(item.key));
        requiresModification = true;
      }

      // VALIDATION RULE 2: Process each active offer
      if (!requiresModification) {
        for (const offer of activeOffers) {
          try {
            const validationResult = validateSingleOffer(offer, currentCart);

            if (validationResult.modified) {
              requiresModification = true;
              allItemKeysToRemove.push(...validationResult.itemKeysToRemove);
              allFreebieItemsToAdd.push(...validationResult.freebieItemsToAdd);
              console.info(`[Freebie System] Offer "${offer.title || offer.id}" - to add: [${validationResult.freebieItemsToAdd.map(f => f.variantId)}], to remove: [${validationResult.itemKeysToRemove}]`);
            } else {
              console.info(`[Freebie System] Offer "${offer.title || offer.id}" - no modifications needed`);
            }
          } catch (offerError) {
            console.warn(
              `[Freebie System] Error processing offer "${offer.title || offer.id || 'Unknown'}", skipping:`,
              offerError
            );
            continue;
          }
        }

        // VALIDATION RULE 3: Handle shared freebies
        const sharedValidation = validateSharedFreebies(activeOffers, currentCart);

        if (sharedValidation.modified) {
          requiresModification = true;
          allItemKeysToRemove.push(...sharedValidation.itemKeysToRemove);
        }

        // VALIDATION RULE 4: Reconcile shared freebies - don't remove a freebie
        // if another UNLOCKED offer still needs it
        if (allItemKeysToRemove.length > 0) {
          const preservedFreebieVariants = new Set();
          const reconciledRemoveKeys = [];

          for (const key of allItemKeysToRemove) {
            const cartItem = currentCart.items.find(i => i.key === key);

            if (!cartItem || !isFreebieItem(cartItem)) {
              reconciledRemoveKeys.push(key);
              continue;
            }

            const isStillNeeded = activeOffers.some(offer =>
              offer.enabled &&
              (offer.freebies || []).includes(cartItem.variant_id) &&
              isOfferUnlocked(offer, currentCart)
            );

            if (isStillNeeded && !preservedFreebieVariants.has(cartItem.variant_id)) {
              preservedFreebieVariants.add(cartItem.variant_id);
              console.info(`[Freebie System] Keeping shared freebie variant ${cartItem.variant_id} - still needed by another unlocked offer`);
            } else {
              reconciledRemoveKeys.push(key);
            }
          }

          allItemKeysToRemove.length = 0;
          allItemKeysToRemove.push(...reconciledRemoveKeys);
        }
      }

      console.info(`[Freebie System] Final plan - requiresModification: ${requiresModification}, toRemove: ${allItemKeysToRemove.length}, toAdd: ${allFreebieItemsToAdd.length}`, { allFreebieItemsToAdd, allItemKeysToRemove });

      // EXECUTE MODIFICATIONS
      if (requiresModification) {
        // Step 1: Remove all items in single batch operation
        if (allItemKeysToRemove.length > 0) {
          currentCart = await batchRemoveCartItems(allItemKeysToRemove);
          await delay(TIMING.batchDelay);
        }

        // Step 2: Add freebies sequentially (Shopify limitation)
        // Out-of-stock freebies are silently skipped — no cart error shown
        for (const freebieItem of allFreebieItemsToAdd) {
          try {
            const addResult = await addFreebieToCart(freebieItem.variantId, freebieItem.offerId);

            if (!addResult.success && addResult.outOfStock) {
              // Silently skip — item is unavailable, no error surfaced to the user
              console.info(`[Freebie System] Skipped out-of-stock freebie variant ${freebieItem.variantId}`);
              continue;
            }

            await delay(TIMING.addDelay);
          } catch (addError) {
            console.warn(`[Freebie System] Failed to add freebie variant ${freebieItem.variantId}, skipping to next:`, addError);
            continue;
          }
        }
      }

      // Step 3: Re-tag shared freebies with the correct active offer ID
      // This runs regardless of requiresModification to fix stale offer tags
      currentCart = await fetchCurrentCart();

      for (const item of currentCart.items) {
        if (!isFreebieItem(item)) continue;

        const correctOffer = activeOffers.find(offer =>
          offer.enabled &&
          (offer.freebies || []).includes(item.variant_id) &&
          isOfferUnlocked(offer, currentCart)
        );

        if (!correctOffer) continue;

        const currentOfferId = item.properties?.[FREEBIE_OFFER_TAG];

        if (currentOfferId !== correctOffer.id) {
          console.info(
            `[Freebie System] Re-tagging freebie variant ${item.variant_id}: ` +
            `offer "${currentOfferId}" → "${correctOffer.id}"`
          );
          try {
            await updateFreebieOfferTag(item.key, correctOffer.id);
            await delay(TIMING.addDelay);
          } catch (tagError) {
            console.warn(`[Freebie System] Failed to re-tag freebie variant ${item.variant_id}, skipping:`, tagError);
          }
        }
      }

      // Step 4: Final cart refresh and notify UI components
      currentCart = await fetchCurrentCart();

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'freebie-script',
          cartData: currentCart
        });
      }

    } catch (error) {
      console.error('[Freebie System] Processing error:', error);
    } finally {
      isProcessingRef.value = false;
    }
  };

  /*****************************************
   * EVENT SETUP
   *****************************************/
  const setupEventListeners = (activeOffers, isProcessingRef) => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'freebie-script') {
          return;
        }

        setTimeout(() => {
          applyFreebieLogic(activeOffers, isProcessingRef);
        }, TIMING.debounce);
      });
    }

    setTimeout(() => {
      applyFreebieLogic(activeOffers, isProcessingRef);
    }, TIMING.initialLoad);
  };

  document.addEventListener('DOMContentLoaded', () => {
    const dataNode = document.getElementById(SELECTORS.dataNode);
    if (dataNode) {
      try {
        const rawData = JSON.parse(dataNode.textContent || '{}');
        console.info('[Freebie System] Raw JSON offers data:', JSON.stringify(rawData.offers, null, 2));
      } catch(e) { /* ignore */ }
    }

    const rawOffers = initializeFreebieSystem();
    console.info('[Freebie System] After validation filter:', rawOffers);
    if (!rawOffers) {
      return;
    }

    const activeOffers = resolveOfferConflicts(rawOffers);
    console.info('[Freebie System] After conflict resolution:', activeOffers.length, 'offer(s) active:', activeOffers.map(o => o.title || o.id));
    if (activeOffers.length === 0) {
      return;
    }

    const isProcessingRef = { value: false };

    setupEventListeners(activeOffers, isProcessingRef);
  });
})();